/**
 * payrollDB.js  —  JSON-as-Database for the Payroll module
 *
 * PATTERN:
 *   - All static/fallback data is read from payrollMockData.json via named query functions.
 *   - Components call these functions to get initial state (skeleton/fallback).
 *   - When the real API responds, components replace the state with live data.
 *   - If the API returns empty/null, the JSON fallback keeps the UI populated.
 *
 * Components NEVER import payrollMockData.json directly.
 */
import db from './payrollMockData.json';

// ── PayrollSummary ────────────────────────────────────────────────────────────
export const getPayrollSummary            = ()  => db.payrollSummary;
export const getKpiCards                  = ()  => db.payrollSummary.kpiCards;
export const getPayrollTrend              = ()  => db.payrollSummary.payrollTrend;
export const getComplianceStatus          = ()  => db.payrollSummary.complianceStatus;
export const getCriticalAlerts            = ()  => db.payrollSummary.criticalAlerts;
export const getStatutoryCompliance       = ()  => db.payrollSummary.statutoryCompliance;
export const getPayoutDistribution        = ()  => db.payrollSummary.payoutDistribution;
export const getQuickInsights             = ()  => db.payrollSummary.quickInsights;
export const getGrossPayrollBreakdown     = ()  => db.payrollSummary.grossPayrollBreakdown;
export const getEmployeePayrollStatus     = ()  => db.payrollSummary.employeePayrollStatus;
export const getDepartmentPayrollSummary  = ()  => db.payrollSummary.departmentPayrollSummary;

// ── Attendance Input ──────────────────────────────────────────────────────────
export const getAttendanceKpis            = ()  => db.attendanceInput.kpis;
export const getApprovalQueueKpis         = ()  => db.attendanceInput.approvalQueueKpis;
export const getAttendanceStatusOptions   = ()  => db.attendanceInput.statusOptions;
export const getAttendanceSourceOptions   = ()  => db.attendanceInput.sourceOptions;
export const getAttendanceFallbackEmployees = () => {
  // Use the full employees array (20 records) and map to attendance fields
  return (db.employees || []).map(e => ({
    name:    e.staffName,
    empId:   e.employeeId,
    dept:    e.department,
    present: e.salaryCountDays?.presentDays  ?? 22,
    lop:     e.salaryCountDays?.lopDays      ?? 1,
    ot:      e.salaryCountDays?.otHours      ?? 4,
    payable: e.salaryCountDays?.payableDays  ?? 22,
    source:  e.payrollStatus === 'Processed' ? 'System'
           : e.payrollStatus === 'Review'    ? 'Manager'
           : 'Manual',
    status:  e.payrollStatus === 'Processed' ? 'Ready'
           : e.payrollStatus === 'Review'    ? 'Review'
           : 'Pending',
    updated: '2h ago',
    avatar:  e.avatar,
  }));
};
export const getAttendanceBarChartData    = ()  => db.attendanceInput.barChartData;
export const getAttendanceLineChartData   = ()  => db.attendanceInput.lineChartData;

// ── Advances & Incentives ─────────────────────────────────────────────────────
export const getAdvancesKpis              = ()  => db.advancesAndIncentives.kpis;
export const getEntryTypes                = ()  => db.advancesAndIncentives.entryTypes;
export const getAdvanceStatusOptions      = ()  => db.advancesAndIncentives.statusOptions;
export const getIncentiveCategories       = ()  => db.advancesAndIncentives.incentiveCategories;
export const getAdvanceCategories         = ()  => db.advancesAndIncentives.advanceCategories;
export const getBonusTypes                = ()  => db.advancesAndIncentives.bonusTypes;
export const getDeductionTypes            = ()  => db.advancesAndIncentives.deductionTypes;
export const getRecoveryMethods           = ()  => db.advancesAndIncentives.recoveryMethods;
export const getAdvanceBranches           = ()  => db.advancesAndIncentives.branches;
export const getAdvanceDepartments        = ()  => db.advancesAndIncentives.departments;

// ── Reimbursement ─────────────────────────────────────────────────────────────
export const getPolicyGroups              = ()  => db.reimbursement.policyGroups;
export const getDefaultPolicyLimits       = ()  => db.reimbursement.defaultPolicyLimits;
export const getPolicyChangeHistory       = ()  => db.reimbursement.policyChangeHistory;
export const getExpenseTypes              = ()  => db.reimbursement.expenseTypes;
export const getReimbursementStatuses     = ()  => db.reimbursement.statusOptions;
export const getReimbursementGrandTotals  = ()  => db.reimbursement.grandTotals;

// ── Run Payroll ───────────────────────────────────────────────────────────────
export const getDefaultPayrollRecord      = ()  => db.runPayroll.defaultPayrollRecord;

// ── Payment History ───────────────────────────────────────────────────────────
export const getPaymentHistoryColumns     = ()  => db.paymentHistory.columns;
export const getPaymentStatusMap          = ()  => db.paymentHistory.statusMap;
export const getPaymentHistoryRecords     = ()  => db.paymentHistoryRecords || [];

// ── Employees (shared across modules) ────────────────────────────────────────
export const getMockEmployees             = ()  => db.employees || [];
export const getAdvanceTransactions       = ()  => db.advanceTransactions || [];
export const getReimbursementClaims       = ()  => db.reimbursementClaims || [];

// ── Shared Filters ────────────────────────────────────────────────────────────
export const getFilterBranches            = ()  => db.filters.branches;
export const getFilterDepartments         = ()  => db.filters.departments;
export const getFilterEmployeeTypes       = ()  => db.filters.employeeTypes;
export const getFilterRegimeTypes         = ()  => db.filters.regimeTypes;
export const getFilterSortOptions         = ()  => db.filters.sortOptions;

// ── Validation Rules ──────────────────────────────────────────────────────────
export const getValidationRules           = (formName) => db.validation[formName] || {};
export const getAllValidationRules         = ()  => db.validation;

// ── API-aware merge helper ────────────────────────────────────────────────────
/**
 * Merges live API data over the JSON fallback.
 * If apiData is null/undefined/empty-array, returns the fallback.
 *
 * @param {*} fallback  - Value from one of the get*() functions above
 * @param {*} apiData   - Live data from Redux / API response
 * @returns {*}         - apiData if truthy and non-empty, else fallback
 */
export function withFallback(fallback, apiData) {
  if (apiData === null || apiData === undefined) return fallback;
  if (Array.isArray(apiData) && apiData.length === 0) return fallback;
  if (typeof apiData === 'object' && !Array.isArray(apiData) && Object.keys(apiData).length === 0) return fallback;
  return apiData;
}
